import { ReactElement, Ref, forwardRef } from 'react';
import { Platform, View, ViewProps } from 'react-native';
import { TIconProps } from '@/common/components/Icons/Icon';
import { SkeletonInline } from '@/common/components/SkeletonInline/SkeletonInline';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import {
  TabBar,
  TabBarTrigger,
} from '@/common/layouts/TabsLayout/TabBar/TabBar';
import { Route, Slot } from 'expo-router';
import { TabList, TabSlot, TabTrigger, Tabs } from 'expo-router/ui';
import { TabBarItem } from '../TabBarItem/TabBarItem';
import { TabsContentWrapper } from './TabsContentWrapper/TabsContentWrapper';
import { TabsTopBar } from './TabsTopBar/TabsTopBar';
import { styles } from './TabsWrapper.styles';

export type TTabsWrapperProps = {
  loading?: boolean;
  items: TTabBarItemOpts[];
};

export function TabsWrapper({ items, loading }: TTabsWrapperProps) {
  const breakpoints = useBreakpoints(['lg']);
  const shouldRenderItem = (item: TTabBarItemOpts) => {
    return !item.hidden && ((breakpoints.lg && item.sidebar) || !item.sidebar);
  };

  if (Platform.OS === 'web') {
    return (
      <View className={styles.wrapper}>
        {!breakpoints.lg && <TabsTopBar />}

        <TabsContentWrapper>
          <Slot />
        </TabsContentWrapper>
        <TabBar>
          {items.map((item) =>
            loading ? (
              <TabBarSkeleton key={item.href} />
            ) : (
              shouldRenderItem(item) && <TabBarItem key={item.href} {...item} />
            )
          )}
        </TabBar>
      </View>
    );
  }

  return (
    <Tabs className={styles.wrapper}>
      {!breakpoints.lg && <TabsTopBar />}
      <TabSlot />
      <TabList asChild>
        <TabBar>
          {items.map((item) => (
            <TabTrigger
              key={item.href}
              name={item.href}
              href={item.href}
              asChild
            >
              {loading ? (
                <TabBarSkeleton />
              ) : (
                shouldRenderItem(item) && (
                  <TabBarTrigger icon={item.icon} label={item.label} />
                )
              )}
            </TabTrigger>
          ))}
          {process.env.EXPO_PUBLIC_ENV !== 'production' && (
            <TabTrigger name="playground" href="/playground" />
          )}
        </TabBar>
      </TabList>
    </Tabs>
  );
}

const TabBarSkeleton = forwardRef((props: ViewProps, ref: Ref<View>) => {
  return (
    <View
      className="mx-sm mt-sm grow justify-center gap-sm lg:mx-0 lg:mt-0 lg:h-[5.2rem] lg:w-full lg:px-xl"
      ref={ref}
    >
      <SkeletonInline className="h-lg" />
      <SkeletonInline className="h-md lg:hidden" />
    </View>
  );
});
TabBarSkeleton.displayName = 'TabBarSkeleton';

export type TTabBarItemOpts = {
  href: Route;
  label: string;
  icon: ReactElement<TIconProps>;
  hidden?: boolean;
  exact?: boolean;
  notification?: boolean;
  sidebar?: boolean;
};
